import React from "react";
import { Link } from "react-router-dom";
import SectionHeader from "./SectionHeader";
import { ArrowRightIcon } from "../../../shared/components/icons";

const nameToSlug = (name) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();

/**
 * Dynamic providers row for the lobby. Mirrors ProvidersPage card styling.
 * Server state arrives via useLobbyProviders (react-query) - this component is
 * presentational and covers loading (skeleton) / error (retry) / empty states.
 *
 * Module-scope + memoized.
 */
const ProvidersSection = ({
  providers = [],
  isLoading,
  isError,
  error,
  onRetry,
  kicker,
  isMobile,
  navigate,
}) => {
  const visible = providers.slice(0, isMobile ? 4 : 5);

  return (
    <section className="mb-14 md:mb-16">
      <SectionHeader
        title="Providers"
        kicker={kicker}
        onViewAll={() => navigate("/providers")}
      />

      {isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
          {Array.from({ length: 10 }).map((_, i) => (
            <div
              key={i}
              className="bg-card rounded-lg h-48 animate-shimmer"
              aria-hidden="true"
            />
          ))}
        </div>
      ) : isError ? (
        <div className="text-center py-8">
          <p className="text-destructive mb-2">
            {error?.message || "We couldn't load providers."}
          </p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="text-primary hover:text-primary/80 font-medium text-sm cursor-pointer active:scale-[0.97] transition-transform"
            >
              Try again
            </button>
          )}
        </div>
      ) : providers.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No providers available right now.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
          {visible.map((provider, i) => (
            <Link
              key={provider.provider_code}
              to={`/games/provider/${nameToSlug(provider.provider_name)}`}
              className="block animate-in fade-in-0 slide-in-from-bottom-2 fill-mode-both duration-300 ease-out motion-reduce:animate-none"
              style={{ animationDelay: `${i * 45}ms` }}
            >
              <div className="bg-card rounded-lg overflow-hidden ring-1 ring-border/60 cursor-pointer group transition-[transform,box-shadow] duration-200 ease-out hover:ring-primary/40 hover:-translate-y-0.5 active:scale-[0.99] motion-reduce:transform-none motion-reduce:transition-none">
                <div className="relative overflow-hidden rounded-lg aspect-[4/3]">
                  {provider.hero_image_url ? (
                    <img
                      src={provider.hero_image_url}
                      alt={`${provider.provider_name} background`}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-105 motion-reduce:transform-none motion-reduce:transition-none"
                      ref={(node) => {
                        if (node && node.complete && node.naturalWidth > 0) {
                          node.classList.add("loaded");
                        }
                      }}
                      onLoad={(e) => e.currentTarget.classList.add("loaded")}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    // Intentional 3-stop brand gradient with alpha; not expressible as a single token.
                    // eslint-disable-next-line no-restricted-syntax
                    <div className="absolute inset-0 bg-gradient-to-br from-[#FFE48A1A] via-[#F5B9551A] to-[#A87A1F1A]" />
                  )}

                  {(provider.logo_image_url || provider.logo_url) && (
                    <div className="absolute inset-0 z-20 pointer-events-none">
                      <div className="absolute inset-0 rounded-lg bg-card/30 backdrop-blur-sm p-4 md:p-6 flex items-center justify-center">
                        <img
                          src={provider.logo_image_url || provider.logo_url}
                          alt={provider.provider_name}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-contain drop-shadow"
                          ref={(node) => {
                            if (node && node.complete && node.naturalWidth > 0) {
                              node.classList.add("loaded");
                            }
                          }}
                          onLoad={(e) => e.currentTarget.classList.add("loaded")}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="absolute bottom-2 right-2 bg-card/70 backdrop-blur-sm text-white text-xs font-medium px-2 py-1 rounded-full z-30 tabular-nums">
                    {provider.game_count ?? 0}{" "}
                    {(provider.game_count ?? 0) === 1 ? "game" : "games"}
                  </div>

                  {provider.website_url && (
                    <a
                      href={provider.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute top-3 right-3 text-foreground hover:text-white cursor-pointer inline-flex items-center text-xs bg-card/30 rounded-full px-2 py-1 z-30"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ArrowRightIcon className="h-3 w-3 mr-1" />
                      Site
                    </a>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
};

export default React.memo(ProvidersSection);
